import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Lock, ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Defined OUTSIDE the page component so it doesn't get re-created on every
// re-render — which would otherwise unmount + remount the <Input> on every
// keystroke and yank focus away from the user.
function PasswordField({ id, label, value, onChange, show, setShow, testIdPrefix, autoComplete }) {
  return (
    <div>
      <Label htmlFor={id} className="flex items-center gap-2 mb-1.5">
        <Lock className="w-4 h-4" /> {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          name={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testIdPrefix}-toggle-visibility`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[32px] min-h-[32px] flex items-center justify-center text-gray-500 hover:text-[#70170F] focus:outline-none focus:ring-2 focus:ring-[#70170F] rounded"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function AccountSecurityPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNext(false); setShowConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (next === current) {
      toast.error('New password must differ from current password');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Password changed successfully');
      reset();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else if (status === 400) {
        toast.error(err.response?.data?.detail || 'Could not change password. Check your inputs.');
      } else {
        toast.error(err.response?.data?.detail || 'Could not change password. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] p-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          data-testid="account-security-back-btn"
          className="inline-flex items-center text-sm text-[#70170F] hover:underline mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>

        <Card className="shadow-md border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#70170F]/10 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#70170F]" />
              </div>
              <div>
                <CardTitle className="text-2xl text-[#1A1A1A]">Account security</CardTitle>
                <CardDescription>Change the password you use to sign in to KEY-MADE.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <PasswordField
                id="current-password"
                label="Current password"
                value={current}
                onChange={setCurrent}
                show={showCurrent}
                setShow={setShowCurrent}
                testIdPrefix="change-password-current"
                autoComplete="current-password"
              />
              <hr className="border-gray-200" />
              <PasswordField
                id="new-password"
                label="New password"
                value={next}
                onChange={setNext}
                show={showNext}
                setShow={setShowNext}
                testIdPrefix="change-password-new"
                autoComplete="new-password"
              />
              <PasswordField
                id="confirm-password"
                label="Confirm new password"
                value={confirm}
                onChange={setConfirm}
                show={showConfirm}
                setShow={setShowConfirm}
                testIdPrefix="change-password-confirm"
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-2">
                Minimum 8 characters. Avoid passwords you've used elsewhere.
              </p>
            </CardContent>
            <CardFooter className="flex justify-end gap-3 border-t border-gray-100 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={reset}
                data-testid="change-password-cancel-btn"
              >
                Clear
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="change-password-submit-btn"
                className="bg-[#70170F] hover:bg-[#5a1209] text-white"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {loading ? 'Updating…' : 'Change password'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
